import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Layers, 
  Sparkles, 
  Zap, 
  MapPin, 
  TrendingUp, 
  Calendar, 
  Cloud, 
  Users, 
  CreditCard, 
  FileText, 
  Building2, 
  ShieldCheck, 
  Command, 
  ArrowRight,
  Globe,
  Sliders,
  X
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '../AuthContext';

interface SaaSCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (tabId: string, subTab?: string) => void;
}

export const SaaSCommandPalette: React.FC<SaaSCommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate
}) => {
  const { vendorProfile } = useAuth();
  const [query, setQuery] = useState('');

  // Handle Escape and Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (isOpen) onClose();
      }
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const allActions = [
    {
      id: 'rfx-opps',
      title: 'Find New RFX Opportunities',
      subtitle: 'Browse active solicitations filtered by your NAICS & NIGP codes',
      category: 'Procurement',
      icon: Layers,
      action: () => onNavigate('rfx-hub', 'opportunities')
    },
    {
      id: 'rfx-sync',
      title: 'Run 50-State Daily Crawler',
      subtitle: 'Harvest live bids across California, Texas, Florida and national portals',
      category: 'Procurement',
      icon: Zap,
      action: () => onNavigate('rfx-hub', 'opportunities')
    },
    {
      id: 'rfx-db',
      title: 'Search Multi-State RFX Database',
      subtitle: 'Full historical and active government contract database',
      category: 'Procurement',
      icon: Layers,
      action: () => onNavigate('rfx-hub', 'database')
    },
    {
      id: 'rfx-mybids',
      title: 'My AI Generated Bid Responses',
      subtitle: 'Review, edit and export previous winning proposals',
      category: 'Proposals',
      icon: FileText,
      action: () => onNavigate('rfx-hub', 'responses')
    },
    {
      id: 'saas-billing',
      title: 'Manage SaaS Plan & Usage Metering',
      subtitle: 'View subscription tiers, token capacity, and PDF invoices',
      category: 'SaaS & Billing',
      icon: CreditCard,
      action: () => onNavigate('saas-hub')
    },
    {
      id: 'saas-team',
      title: 'Team & Seat Permissions (RBAC)',
      subtitle: 'Invite proposal drafters, compliance auditors, and managers',
      category: 'SaaS & Billing',
      icon: Users,
      action: () => onNavigate('saas-hub')
    },
    {
      id: 'saas-api',
      title: 'Developer API & Webhooks Hub',
      subtitle: 'Configure REST API keys and real-time CRM webhook streaming',
      category: 'SaaS & Billing',
      icon: Command,
      action: () => onNavigate('saas-hub')
    },
    {
      id: 'workspace-drive',
      title: 'Google Workspace Integration',
      subtitle: 'Sync proposal documents to Google Drive, Docs & Sheets',
      category: 'Integrations',
      icon: Cloud,
      action: () => onNavigate('workspace')
    },
    {
      id: 'state-profiles',
      title: 'State Vendor Registries & Portal Direct',
      subtitle: '50-State portal links, vendor requirements, and bidding codes',
      category: 'Intelligence',
      icon: MapPin,
      action: () => onNavigate('state-profiles')
    },
    {
      id: 'state-budget',
      title: 'State Budget Forecast & NIGP Allocations',
      subtitle: 'Fiscal year spending forecasts and agency procurement budgets',
      category: 'Intelligence',
      icon: TrendingUp,
      action: () => onNavigate('state-budget')
    },
    {
      id: 'federal-portal',
      title: 'Federal SAM.gov & Co-op Center',
      subtitle: 'National cooperative purchasing schedules and federal feeds',
      category: 'Intelligence',
      icon: ShieldCheck,
      action: () => onNavigate('federal-portal')
    },
    {
      id: 'reminders',
      title: 'Bid Deadlines Calendar & Reminders',
      subtitle: 'Schedule bid submission timelines and sync to Google Calendar',
      category: 'Productivity',
      icon: Calendar,
      action: () => onNavigate('reminders')
    },
    {
      id: 'profile',
      title: 'Vendor Profile & Capabilities',
      subtitle: 'Update company EIN, CAGE code, SAM UEI and capabilities',
      category: 'Account',
      icon: Building2,
      action: () => onNavigate('profile')
    }
  ];

  const filtered = allActions.filter(a => 
    a.title.toLowerCase().includes(query.toLowerCase()) || 
    a.subtitle.toLowerCase().includes(query.toLowerCase()) ||
    a.category.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-50 flex items-start justify-center pt-20 p-4 animate-fade-in">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: -10 }}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
      >
        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-3 bg-gray-50/50">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            type="text"
            autoFocus
            placeholder="Type a command, state portal, feature or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent border-none text-sm text-gray-900 placeholder-gray-400 outline-none font-medium"
          />
          <span className="text-[10px] font-mono font-bold text-gray-400 bg-gray-200 px-2 py-0.5 rounded">
            ESC
          </span>
        </div>

        {/* Results List */}
        <div className="max-h-96 overflow-y-auto p-2 space-y-1">
          {filtered.length > 0 ? (
            filtered.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  item.action();
                  onClose();
                }}
                className="w-full p-3 rounded-xl hover:bg-indigo-50/80 flex items-center justify-between text-left transition-colors group cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-gray-100 group-hover:bg-indigo-600 text-gray-600 group-hover:text-white transition-colors shrink-0">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-xs text-gray-900 group-hover:text-indigo-950 truncate">{item.title}</p>
                      <span className="text-[9px] font-bold text-gray-400 uppercase bg-gray-100 px-1.5 py-0.2 rounded">
                        {item.category}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 truncate mt-0.5">{item.subtitle}</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-all shrink-0 ml-2" />
              </button>
            ))
          ) : (
            <div className="py-8 text-center text-gray-400 text-xs">
              No matching actions or features found for "{query}".
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-medium px-4">
          <span>Navigate with <kbd className="font-mono bg-white border px-1 rounded text-[10px]">↑</kbd> <kbd className="font-mono bg-white border px-1 rounded text-[10px]">↓</kbd></span>
          <span>RFXpress SaaS Workspace: <strong className="text-gray-700">{vendorProfile?.companyName || 'QuantumAI Inc.'}</strong></span>
        </div>
      </motion.div>
    </div>
  );
};
